// @ts-nocheck
// CTI right rail — permanent sidebar on the right of the CTI shell.
// Three tabs (History / Contacts / Call details) that stay open across
// every screen (idle / voicemail / leads / follow-ups / on-call / wrap).
//
// Data sources — all already integrated elsewhere in the app, no new
// endpoint added:
//
//   • History      — agentCallsData.callRecords, passed in as a prop.
//                    Same feed as the left-rail Recent Calls and the
//                    workspace Call Log panel. Sourced from
//                    API_AGENT_DASHBOARD_CALLS (polled every 6 s from
//                    CTIAgentUI). Rendered as the full interaction
//                    list — direction icon + name + timestamp + duration
//                    + disposition badge (Callback / Interested / …).
//
//   • Contacts     — API_ALL_CONTACTS_LIST fetched on mount + polled
//                    every 30 s while the tab is active. Returns the
//                    agent's assigned phonebook (contact-group members
//                    the agent has access to).
//
//   • Call details — read from useCallStore ambient state (activeCallMeta,
//                    callState, direction, activeCliNumber, etc.). No
//                    fetch needed — populated live by the SIP.js client
//                    and the mobile-call socket.
//
// Rendered as an <aside> so it participates in the CTI shell's
// accessibility tree the same way the left dialer rail does. Styling
// lives in cti.css under `.cti-right-rail` — mirrors the left rail's
// surface/border/scroll behaviour so both rails read as bookend columns.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  FiClock,
  FiUsers,
  FiInfo,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiPhoneMissed,
  FiPhoneOff,
  FiSearch,
  FiRefreshCw,
  FiBell,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiPhoneCall,
  FiBookOpen,
} from 'react-icons/fi';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_ALL_CONTACTS_LIST,
  API_CONTACT_GROUP_MEMBERS_CREATE,
  API_CONTACT_GROUP_MEMBERS_UPDATE,
  API_CONTACT_GROUP_MEMBERS_DELETE,
  API_AGENT_MY_INTERNAL_AGENTS,
} from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { maskNumber } from '@/utilities/cliDisplay';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCallStore } from '@/router/zustand/callStore';
import { mosToLabel } from '@/service/webrtcCallStats';
import {
  agentCallStatus,
  isIncomingDirection,
  callRowCustomerNumber,
  CALL_STATUS_LABELS,
  callDurationSeconds,
  callPhaseEvt,
  callStateEvt,
  formatSeconds,
  resolveCallPhase,
  resolveCustomerName,
  resolveCustomerNumber,
  HAIRLINE,
} from '../ctiHelpers';

const TABS = [
  { key: 'history',      label: 'History',      icon: FiClock },
  { key: 'phonebook',    label: 'Phonebook',    icon: FiBookOpen },
  { key: 'call-details', label: 'Call details', icon: FiInfo },
];

function formatShortDateTime(value) {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: String(value), time: '' };
  const day = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return { date: day, time };
}

function dispositionTone(category) {
  const key = String(category || '').toLowerCase().replace(/\s+/g, '-');
  if (['interested', 'converted', 'follow-up', 'callback-scheduled'].includes(key))
    return 'good';
  if (['not-interested', 'declined', 'busy'].includes(key)) return 'bad';
  if (['callback', 'call-back'].includes(key)) return 'info';
  if (['no-response', 'abandoned', 'missed', 'ivr-only', 'ivr', 'unknown'].includes(key))
    return 'mute';
  return 'default';
}

function directionIcon(record) {
  const isIncoming = isIncomingDirection(record);
  const status = agentCallStatus(record);
  const isAnswered = status
    ? status === 'connected'
    : record?.answer_status === 'answered' || Boolean(record?.answered);
  const hangupCause = String(record?.hangup_cause || '').toLowerCase();
  const declinedByAgent =
    record?.is_declined === true ||
    (record?.is_missed !== true &&
      (hangupCause.includes('declined_by_agent') || hangupCause.includes('declined')));

  if (isAnswered) {
    return isIncoming ? <FiArrowDownLeft /> : <FiArrowUpRight />;
  }
  if (isIncoming) {
    return declinedByAgent ? <FiPhoneOff /> : <FiPhoneMissed />;
  }
  return <FiPhoneOff />;
}

function lastTen(n) {
  return String(n || '').replace(/\D/g, '').slice(-10);
}

// A customerHistory entry is a saved DISPOSITION (API_CUSTOMER_DISPOSITION_
// HISTORY, disposition-form-data-controller.js's getCustomerHistory), not a
// raw call record — it exists precisely because some agent (any agent, not
// just the current one) handled and dispositioned this caller before, which
// callRecords (this agent's own call log, from agentCallsData) can't see
// across agents. Confirmed response shape: { disposition_form_data_id,
// agent_id, agent_name, campaign_id, campaign_name, call_sid,
// disposition_source, remarks_status: [], data: {...}, created_at,
// edited_at } — no `contact` object, no call direction, no duration. `data`
// is the raw typed-field blob keyed by the disposition form's OWN field
// names verbatim (whatever the admin typed — "firstame"/"lastName"/etc. for
// a form with custom fields, or the generic first_name/last_name/
// contact_number keys for a form with none) — same fuzzy first/last-name
// match `useDispositionFlow.ts` uses on submit, since the display name has
// to survive the same admin typos the sync does.
function isOneEditOrLess(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0; let j = 0; let foundDiff = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] !== longer[j]) {
      if (foundDiff) return false;
      foundDiff = true;
      if (shorter.length === longer.length) i++;
      j++;
    } else { i++; j++; }
  }
  return true;
}
const normalizeDataKey = (k) => String(k || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

function findFuzzyDataValue(data, canonical) {
  if (!data || typeof data !== 'object') return '';
  for (const key of Object.keys(data)) {
    if (isOneEditOrLess(normalizeDataKey(key), canonical)) {
      const v = data[key];
      if (v !== null && v !== undefined && String(v).trim() !== '') return String(v);
    }
  }
  return '';
}

function extractContactName(h) {
  const data = h?.data && typeof h.data === 'object' ? h.data : {};
  const first = findFuzzyDataValue(data, 'firstname');
  const last = findFuzzyDataValue(data, 'lastname');
  return `${first} ${last}`.trim();
}

// Every other field the agent actually typed into the disposition form —
// name (already shown separately), remarks_status (already shown as the
// category/free-text note), the contact number (this pane is already
// scoped to that one number) and follow-up bookkeeping (enabled/date/time —
// internal state, not something the agent "filled in") are excluded —
// rendered as "Field: value" so whatever else the form asked for shows up
// here too, not just the fields this pane happens to know the meaning of.
function extractExtraFields(h) {
  const data = h?.data && typeof h.data === 'object' ? h.data : {};
  return Object.keys(data)
    .filter((key) => {
      const norm = normalizeDataKey(key);
      return (
        norm !== 'remarksstatus' &&
        !norm.startsWith('followup') &&
        !isOneEditOrLess(norm, 'firstname') &&
        !isOneEditOrLess(norm, 'lastname') &&
        !isOneEditOrLess(norm, 'contactnumber')
      );
    })
    .map((key) => {
      const v = data[key];
      if (v === null || v === undefined || String(v).trim() === '') return null;
      return `${key}: ${v}`;
    })
    .filter(Boolean);
}

function mapCustomerHistoryRow(h) {
  const stamp = h?.created_at || h?.edited_at;
  const { date, time } = formatShortDateTime(stamp);
  const statuses = Array.isArray(h?.remarks_status) ? h.remarks_status.filter(Boolean) : [];
  const freeTextNote = statuses.length > 1 ? statuses.slice(1).join(', ') : '';
  const note = [freeTextNote, ...extractExtraFields(h)].filter(Boolean).join(' · ');
  return {
    id: h?.disposition_form_data_id || h?.call_sid || stamp,
    icon: <FiClock />,
    date,
    time,
    name: extractContactName(h) || 'Unnamed contact',
    num: '',
    duration: '',
    category: statuses[0] || '',
    note,
  };
}

// An outgoing call that never got answered used to always read "Declined"
// here too — including ones that failed at the carrier before ever
// ringing (e.g. "Unallocated (unassigned) number"). Same fix as
// dialerFormat.tsx's useRecentCalls.
function isFailedOutgoingHangup(hangupCause) {
  const cause = String(hangupCause || '').toLowerCase();
  return cause.includes('unalloc')
    || cause.includes('unassigned')
    || cause.includes('invalid number')
    || cause.includes('no route')
    || cause.includes('not reachable')
    || cause.includes('congestion')
    || cause.includes('out of order')
    || cause.includes('failed');
}

function mapCallRecordRow(r, contactNameByNumber) {
  const stamp = r?.time_of_call || r?.created_at;
  const { date, time } = formatShortDateTime(stamp);
  const status = agentCallStatus(r);
  const isAnswered = status ? status === 'connected' : r?.answer_status === 'answered' || Boolean(r?.answered);
  const isIncoming = isIncomingDirection(r);
  const notAnsweredLabel = status
    ? CALL_STATUS_LABELS[status]
    : isIncoming ? 'Missed' : (isFailedOutgoingHangup(r?.hangup_cause) ? 'Failed' : 'Declined');
  const num = callRowCustomerNumber(r);
  // Unmasked number for redial — see dialerFormat.tsx's useRecentCalls.
  const dialNum = r?.dial_number || (/[xX*]/.test(num) ? '' : num);
  // The backend's own name resolution (r.name/caller_profile) is empty for
  // most calls, showing "Unknown" even when the number matches one of the
  // agent's own saved contacts — same cross-reference Dialer.tsx's Recent
  // Calls list uses (dialerFormat.tsx's useRecentCalls).
  const contactName = contactNameByNumber?.[String(dialNum || num).replace(/\D/g, '').slice(-10)];
  return {
    id: r?.id ?? `${stamp}-${r?.caller_number}`,
    icon: directionIcon(r),
    date,
    time,
    dialNum,
    name: r?.name || r?.caller_profile || contactName || 'Unknown',
    num,
    duration: isAnswered ? formatSeconds(callDurationSeconds(r)) : notAnsweredLabel,
    category: r?.category || (isAnswered ? '' : notAnsweredLabel),
    note: r?.disposition_note || r?.note || r?.remark || '',
  };
}

function HistoryPane({ callRecords, customerHistory, customerHistoryLoading, onCallNumber, activeCustomerNumber, maskNumbers, contactNameByNumber }) {
  const [query, setQuery] = useState('');
  const activeKey = useMemo(() => lastTen(activeCustomerNumber), [activeCustomerNumber]);
  // customerHistory is cross-agent (any agent's saved dispositions on this
  // number) and the right data for "this caller's last 5 calls" — prefer it
  // whenever the caller actually supplies it (cti2's AgentPortalPage does).
  // Production's CTIAgentUI.tsx call site doesn't have that cache yet, so
  // it leaves this prop undefined and falls back to filtering callRecords
  // (this agent's own log) as before, rather than going blank.
  const hasCustomerHistoryFeed = Array.isArray(customerHistory);

  const rows = useMemo(() => {
    // No active call → no caller to scope to → this pane stays empty. It's
    // a per-caller history strip ("this caller's last 5"), not a general
    // call log — Recent Calls (Dialer rail) already covers that, always.
    if (!activeKey) return [];
    const q = query.trim().toLowerCase();

    if (hasCustomerHistoryFeed) {
      const filtered = q
        ? customerHistory.filter((h) => {
            const agent = String(h?.agent_name || h?.agent || '').toLowerCase();
            const statuses = Array.isArray(h?.remarks_status) ? h.remarks_status.join(' ').toLowerCase() : '';
            return agent.includes(q) || statuses.includes(q);
          })
        : customerHistory;
      return filtered.slice(0, 5).map(mapCustomerHistoryRow);
    }

    if (!Array.isArray(callRecords)) return [];
    const scoped = callRecords.filter(
      (r) => lastTen(callRowCustomerNumber(r)) === activeKey,
    );
    const filtered = q
      ? scoped.filter((r) => {
          const name = String(r?.name || r?.caller_profile || '').toLowerCase();
          const num = String(callRowCustomerNumber(r)).toLowerCase();
          return name.includes(q) || num.includes(q);
        })
      : scoped;
    return filtered.slice(0, 5).map((r) => mapCallRecordRow(r, contactNameByNumber));
  }, [callRecords, customerHistory, hasCustomerHistoryFeed, query, activeKey, contactNameByNumber]);

  return (
    <div className="cri-pane cri-pane--history">
      <div className="cri-panel">
        <div className="cri-panel__head">
          <FiClock size={13} className="cri-panel__ico" />
          <span className="cri-panel__title">Disposition Call history</span>
          {activeKey ? (
            <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
              This caller
            </span>
          ) : null}
          <span className="cri-panel__count">
            {rows.length} {rows.length === 1 ? 'interaction' : 'interactions'}
          </span>
        </div>
        {query || rows.length > 3 ? (
          <div className="cri-panel__filter">
            <FiSearch size={11} className="cri-search__icon" />
            <input
              type="text"
              placeholder="Search history"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="cri-search__input"
            />
          </div>
        ) : null}
        <div className="cri-hist-list">
          {rows.length === 0 ? (
            <div className="cri-empty">
              {!activeKey
                ? 'Disposition History shows here once a call comes in'
                : hasCustomerHistoryFeed && customerHistoryLoading
                  ? 'Loading…'
                  : 'No previous disposition calls from this number'}
            </div>
          ) : (
            rows.map((r) => {
              const tone = dispositionTone(r.category);
              return (
                <button
                  key={r.id}
                  type="button"
                  className="cri-hist-row"
                  onClick={() => {
                    const target = r.dialNum !== undefined ? r.dialNum : r.num;
                    if (target) onCallNumber?.(target);
                  }}
                  title={r.num ? `Redial ${maskNumbers ? maskNumber(r.num) : r.num}` : ''}
                >
                  <div className="cri-hist-line">
                    <span className="cri-hist-ico">{r.icon}</span>
                    <span className="cri-hist-at">
                      {r.date} · {r.time}
                    </span>
                    {r.category ? (
                      <span className={`cri-hist-dispo cri-hist-dispo--${tone}`}>
                        {r.category}
                      </span>
                    ) : null}
                  </div>
                  <div className="cri-hist-agent">
                    <span className="cri-hist-name">{r.name}</span>
                    <span className="cri-hist-dur">{r.duration}</span>
                  </div>
                  {r.note ? <p className="cri-hist-note">{r.note}</p> : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// Field chrome ported verbatim from CreateAllContactsPage.tsx / ContactEditPage.tsx
// (label + input stack, "Ex. …" placeholders) so the in-rail dialog reads the
// same as the full-page admin forms it stands in for.
const NO_SPECIAL_CHAR_REGEX = /^[a-zA-Z0-9 ]*$/;

function ContactField({ label, required, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-foreground/80">
        {label} {required ? <span className="text-destructive">*</span> : null}
      </label>
      {children}
    </div>
  );
}

const contactInputCls =
  'form-control h-9 w-full rounded-md border border-input bg-background px-2.5 text-sm text-foreground outline-none focus:border-primary';

function contactDisplayName(c) {
  const resolved =
    `${c?.first_name || ''} ${c?.last_name || ''}`.trim() ||
    c?.contact_name ||
    c?.name ||
    '';
  // The backend can default contact_name/name to the phone number itself
  // when no real name was ever set — showing that back as if it were a
  // name is confusing (looks like a saved contact when it isn't one).
  const numDigits = String(c?.primary_number || c?.contact_number || c?.number || '').replace(/\D/g, '');
  const looksLikeNumber = resolved && numDigits && resolved.replace(/\D/g, '') === numDigits;
  if (!resolved || looksLikeNumber) return c?.id ? `Unknown (#${c.id})` : 'Unknown';
  return resolved;
}

// Email/Language have no dedicated column on a contact — they're saved
// into additional_details (a JSONB blob) via AddContactForm/EditContactForm
// below, so read back with the same fuzzy key matching CustomerProfileCard
// already uses for the live-call header (duplicated here rather than
// imported — it's a small local helper, not exported there).
const normalizeAdditionalDetailKey = (name) => String(name || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
function isOneEditOrLessApart(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0; let j = 0; let foundDiff = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] !== longer[j]) {
      if (foundDiff) return false;
      foundDiff = true;
      if (shorter.length === longer.length) { i++; j++; } else { j++; }
    } else { i++; j++; }
  }
  return true;
}
function findAdditionalDetail(details, ...candidates) {
  if (!details || typeof details !== 'object') return null;
  for (const [k, v] of Object.entries(details)) {
    const nk = normalizeAdditionalDetailKey(k);
    if (!candidates.some((c) => isOneEditOrLessApart(nk, c))) continue;
    if (v === null || v === undefined || typeof v === 'object') continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
}

// Mirrors CreateAllContactsPage.tsx's local state/handlers/validation —
// same "mark one number as primary" UX, ported in place of navigating to
// /create-all-contacts (which would drop the agent out of the call screen).
function AddContactForm({ onClose, onSaved }) {
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    state: '',
    country: '',
    primaryNumber: '',
    isPrimaryNumber: false,
    alternateNumbers: [{ number: '', isPrimary: false }],
    company: '',
    email: '',
  });
  const [saving, setSaving] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const fieldLabels = {
      firstName: 'First name',
      lastName: 'Last name',
      street: 'Street',
      city: 'City',
      state: 'State',
      country: 'Country',
      company: 'Company',
    };
    if (name in fieldLabels && !NO_SPECIAL_CHAR_REGEX.test(value)) {
      toast.error(`${fieldLabels[name]} must not contain special characters`);
      return;
    }
    setContact((prev) => ({ ...prev, [name]: value }));
  };

  const handlePrimaryNumberCheck = (e) => {
    const { checked } = e.target;
    setContact((prev) => ({
      ...prev,
      isPrimaryNumber: checked,
      alternateNumbers: prev.alternateNumbers.map((num) => ({ ...num, isPrimary: false })),
    }));
  };

  const handleAlternateNumberChange = (index, field, value) => {
    setContact((prev) => {
      const newAlt = [...prev.alternateNumbers];
      if (field === 'isPrimary') {
        newAlt.forEach((num, i) => {
          num.isPrimary = i === index ? !newAlt[index].isPrimary : false;
        });
        return { ...prev, isPrimaryNumber: false, alternateNumbers: newAlt };
      }
      newAlt[index] = { ...newAlt[index], [field]: value };
      return { ...prev, alternateNumbers: newAlt };
    });
  };

  const addAlternateNumber = () => {
    setContact((prev) => ({
      ...prev,
      alternateNumbers: [...prev.alternateNumbers, { number: '', isPrimary: false }],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      firstName,
      lastName,
      primaryNumber,
      street,
      city,
      state,
      country,
      company,
      email,
      alternateNumbers = [],
    } = contact;

    const primaryAlternate = alternateNumbers.find((num) => num.isPrimary);
    const finalPrimaryNumber = primaryAlternate ? primaryAlternate.number : primaryNumber;

    const allNumbers = [
      ...alternateNumbers.filter((num) => !num.isPrimary).map((num) => num.number),
      ...(primaryAlternate ? [primaryNumber] : []),
    ].filter((num) => num && num.trim() !== '' && num !== finalPrimaryNumber);
    const [alt1_number, alt2_number] = allNumbers.slice(0, 2);

    if (!firstName.trim() || !finalPrimaryNumber.trim()) {
      toast.error('First name and primary number are required');
      return;
    }
    if (!NO_SPECIAL_CHAR_REGEX.test(firstName.trim())) {
      toast.error('First name must not contain special characters');
      return;
    }
    if (city.trim() && !NO_SPECIAL_CHAR_REGEX.test(city.trim())) {
      toast.error('City must not contain special characters');
      return;
    }
    const hasPrimaryAlternate = alternateNumbers.some((num) => num.isPrimary);
    if (!contact.isPrimaryNumber && !hasPrimaryAlternate) {
      toast.error('Please mark at least one phone number as primary');
      return;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error('Enter a valid email address');
      return;
    }

    const additionalDetails = {};
    if (email.trim()) additionalDetails.email = email.trim();

    const payload = {
      first_name: firstName,
      last_name: lastName,
      street,
      city,
      state,
      country,
      company,
      primary_number: finalPrimaryNumber,
      alt1_number: alt1_number || null,
      alt2_number: alt2_number || null,
      ...(Object.keys(additionalDetails).length ? { additional_details: additionalDetails } : {}),
    };

    setSaving(true);
    try {
      await postApiCall(API_CONTACT_GROUP_MEMBERS_CREATE, payload);
      toast.success('Contact created successfully');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  const primaryLen = (contact.primaryNumber || '').length;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ContactField label="First name" required>
          <input
            type="text"
            className={contactInputCls}
            name="firstName"
            value={contact.firstName}
            onChange={handleInputChange}
            placeholder="Ex. John"
            required
          />
        </ContactField>
        <ContactField label="Last name">
          <input
            type="text"
            className={contactInputCls}
            name="lastName"
            value={contact.lastName}
            onChange={handleInputChange}
            placeholder="Ex. Smith"
          />
        </ContactField>
      </div>

      <div>
        <label className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground/80">
          <span>
            Primary Phone Number <span className="text-destructive">*</span>
          </span>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-primary"
              checked={contact.isPrimaryNumber}
              onChange={handlePrimaryNumberCheck}
            />
            Use this as primary
          </label>
        </label>
        <input
          type="tel"
          className={contactInputCls}
          value={contact.primaryNumber}
          onChange={(e) =>
            setContact((prev) => ({ ...prev, primaryNumber: e.target.value.replace(/\D/g, '').slice(0, 10) }))
          }
          placeholder="Ex. 0123456789"
          required
          maxLength={10}
        />
        <small className={`mt-1 block text-[11px] ${primaryLen === 10 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
          {primaryLen === 10 ? '✓ Valid 10-digit number' : 'Enter exactly 10 digits (numbers only)'}
        </small>
      </div>

      {contact.alternateNumbers.map((alt, index) => (
        <div key={index}>
          <label className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground/80">
            <span>{index === 0 ? 'Alternate Phone Number' : `Alternate Phone Number ${index + 1}`}</span>
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-primary"
                checked={alt.isPrimary}
                onChange={() => handleAlternateNumberChange(index, 'isPrimary')}
              />
              Use this as primary
            </label>
          </label>
          <input
            type="tel"
            className={contactInputCls}
            value={alt.number}
            onChange={(e) =>
              handleAlternateNumberChange(index, 'number', e.target.value.replace(/\D/g, '').slice(0, 10))
            }
            placeholder="Ex. 0123456789"
            maxLength={10}
          />
        </div>
      ))}

      {contact.alternateNumbers.length < 2 ? (
        <Button variant="outline" size="sm" type="button" onClick={addAlternateNumber} className="gap-1">
          <FiPlus className="h-4 w-4" />
          Add phone number
        </Button>
      ) : null}

      <ContactField label="Street">
        <input
          type="text"
          className={contactInputCls}
          name="street"
          value={contact.street}
          onChange={handleInputChange}
          placeholder="Ex. Ninth Street"
        />
      </ContactField>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ContactField label="City">
          <input type="text" className={contactInputCls} name="city" value={contact.city} onChange={handleInputChange} placeholder="Ex. New York" />
        </ContactField>
        <ContactField label="State / Province">
          <input type="text" className={contactInputCls} name="state" value={contact.state} onChange={handleInputChange} placeholder="Ex. NY" />
        </ContactField>
        <ContactField label="Country">
          <input type="text" className={contactInputCls} name="country" value={contact.country} onChange={handleInputChange} placeholder="Ex. USA" />
        </ContactField>
      </div>

      <ContactField label="Company">
        <input type="text" className={contactInputCls} name="company" value={contact.company} onChange={handleInputChange} placeholder="Ex. Voxtech Co" />
      </ContactField>

      <ContactField label="Email">
        <input
          type="email"
          className={contactInputCls}
          name="email"
          value={contact.email}
          onChange={(e) => setContact((prev) => ({ ...prev, email: e.target.value }))}
          placeholder="Ex. john@example.com"
        />
      </ContactField>

      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Creating…' : 'Create Contact'}
        </Button>
      </DialogFooter>
    </form>
  );
}

// Mirrors ContactEditPage.tsx — flat editable primary/alt numbers, no
// primary-swap checkboxes (that's create-only in the original app).
function EditContactForm({ initial, onClose, onSaved, maskNumbers = false }) {
  const [contact, setContact] = useState({
    ...initial,
    email: initial?.additional_details?.email || '',
  });
  const [saving, setSaving] = useState(false);

  const setField = (name, value) => setContact((prev) => ({ ...prev, [name]: value }));
  const handleInputChange = (e) => setField(e.target.name, e.target.value.replace(/^\s+/, ''));
  const handleBlurTrim = (e) => setField(e.target.name, (e.target.value ?? '').trim());

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = {
      first_name: contact.first_name?.trim(),
      last_name: contact.last_name?.trim(),
      street: contact.street?.trim(),
      city: contact.city?.trim(),
      state: contact.state?.trim(),
      country: contact.country?.trim(),
      company: contact.company?.trim(),
      primary_number: contact.primary_number?.trim(),
      alt1_number: contact.alt1_number?.trim(),
      alt2_number: contact.alt2_number?.trim(),
    };

    if (!trimmed.first_name || !trimmed.primary_number) {
      toast.error('First name and primary number are required');
      return;
    }
    if (!/^\d{10}$/.test(trimmed.primary_number)) {
      toast.error('Primary number must be exactly 10 digits');
      return;
    }
    if (trimmed.alt1_number && !/^\d{10}$/.test(trimmed.alt1_number)) {
      toast.error('Alternate number 1 must be exactly 10 digits or empty');
      return;
    }
    if (trimmed.alt2_number && !/^\d{10}$/.test(trimmed.alt2_number)) {
      toast.error('Alternate number 2 must be exactly 10 digits or empty');
      return;
    }
    if (!NO_SPECIAL_CHAR_REGEX.test(trimmed.first_name)) {
      toast.error('First name must not contain special characters');
      return;
    }
    if (trimmed.city && !NO_SPECIAL_CHAR_REGEX.test(trimmed.city)) {
      toast.error('City must not contain special characters');
      return;
    }
    const trimmedEmail = (contact.email || '').trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('Enter a valid email address');
      return;
    }

    const existingDetails = (initial?.additional_details && typeof initial.additional_details === 'object' && !Array.isArray(initial.additional_details))
      ? initial.additional_details
      : {};
    // language dropped entirely — no real DB column backs it, and it always
    // defaulted to "English" regardless of the contact's actual language,
    // so it was a misleading always-present falsy value rather than real
    // data. Also strips it from any contact edited from here that still
    // has a stale value from before this was removed.
    const additionalDetails = { ...existingDetails };
    delete additionalDetails.language;
    if (trimmedEmail) additionalDetails.email = trimmedEmail; else delete additionalDetails.email;

    const payload = {
      ...trimmed,
      alt1_number: trimmed.alt1_number || null,
      alt2_number: trimmed.alt2_number || null,
      additional_details: additionalDetails,
    };

    setSaving(true);
    try {
      await postApiCall(API_CONTACT_GROUP_MEMBERS_UPDATE.replace(':id', contact.id), payload);
      toast.success('Contact updated successfully');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ContactField label="First Name" required>
          <input type="text" className={contactInputCls} name="first_name" value={contact.first_name || ''} onChange={handleInputChange} onBlur={handleBlurTrim} required />
        </ContactField>
        <ContactField label="Last Name">
          <input type="text" className={contactInputCls} name="last_name" value={contact.last_name || ''} onChange={handleInputChange} onBlur={handleBlurTrim} />
        </ContactField>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ContactField label="Primary Number" required>
          {/* Masked agents see the saved number masked and can't edit it —
            * typing into a masked value would corrupt the real number. */}
          <input
            type="tel"
            className={contactInputCls}
            name="primary_number"
            value={maskNumbers ? maskNumber(contact.primary_number || '') : contact.primary_number || ''}
            onChange={(e) => setField('primary_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
            onBlur={maskNumbers ? undefined : handleBlurTrim}
            maxLength={10}
            readOnly={maskNumbers}
            required
          />
        </ContactField>
        <ContactField label="Alternate Number 1">
          <input
            type="tel"
            className={contactInputCls}
            name="alt1_number"
            value={contact.alt1_number || ''}
            onChange={(e) => setField('alt1_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
            onBlur={handleBlurTrim}
            maxLength={10}
          />
        </ContactField>
        <ContactField label="Alternate Number 2">
          <input
            type="tel"
            className={contactInputCls}
            name="alt2_number"
            value={contact.alt2_number || ''}
            onChange={(e) => setField('alt2_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
            onBlur={handleBlurTrim}
            maxLength={10}
          />
        </ContactField>
      </div>

      <ContactField label="Street Address">
        <input type="text" className={contactInputCls} name="street" value={contact.street || ''} onChange={handleInputChange} onBlur={handleBlurTrim} />
      </ContactField>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ContactField label="City">
          <input type="text" className={contactInputCls} name="city" value={contact.city || ''} onChange={handleInputChange} onBlur={handleBlurTrim} />
        </ContactField>
        <ContactField label="State / Province">
          <input type="text" className={contactInputCls} name="state" value={contact.state || ''} onChange={handleInputChange} onBlur={handleBlurTrim} />
        </ContactField>
        <ContactField label="Country">
          <input type="text" className={contactInputCls} name="country" value={contact.country || ''} onChange={handleInputChange} onBlur={handleBlurTrim} />
        </ContactField>
      </div>

      <ContactField label="Company">
        <input type="text" className={contactInputCls} name="company" value={contact.company || ''} onChange={handleInputChange} onBlur={handleBlurTrim} />
      </ContactField>

      <ContactField label="Email">
        <input
          type="email"
          className={contactInputCls}
          name="email"
          value={contact.email || ''}
          onChange={handleInputChange}
          onBlur={handleBlurTrim}
          placeholder="Ex. john@example.com"
        />
      </ContactField>

      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  );
}

function ContactFormDialog({ open, mode, initial, onClose, onSaved, maskNumbers = false }) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="cri-modal-content max-h-[85vh] max-w-2xl overflow-y-auto" overlayClassName="cri-modal-overlay">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Contact' : 'Create Contact'}</DialogTitle>
        </DialogHeader>
        {open ? (
          mode === 'edit' ? (
            <EditContactForm initial={initial} onClose={onClose} onSaved={onSaved} maskNumbers={maskNumbers} />
          ) : (
            <AddContactForm onClose={onClose} onSaved={onSaved} />
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DeleteContactDialog({ contact, deleting, onClose, onConfirm }) {
  return (
    <Dialog open={!!contact} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="cri-modal-content max-w-sm" overlayClassName="cri-modal-overlay">
        <DialogHeader>
          <DialogTitle>Delete contact</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Delete <span className="font-medium text-foreground">{contact ? contactDisplayName(contact) : ''}</span>?
          This action cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Internal directory — every agent created by the same parent as the
// logged-in agent (i.e. the same call creator), team lead included. Uses
// API_AGENT_MY_INTERNAL_AGENTS (agents/me/internal-agents) — same source the
// old cloudtelephony_frontend phonebook uses — rather than
// API_AGENT_TRANSFER_AVAILABLE, which only returns agents that are already
// free to take a transfer right now and drops every busy/offline agent from
// the creator, plus hardcodes availability_status to "Available". This
// endpoint returns the full roster with a real per-agent availability
// (available / busy / offline) computed from login_status + call_status +
// is_submitting_disposition + break state.
// Two sub-tabs, same split as old ui's "Agent Phonebook": Internal (agents
// from the same call creator, with live availability) and External. External
// just mounts ContactsPane as-is — that's the one place add/edit/delete +
// ACL gating for phonebook contacts lives, so folding it in here means
// reusing it whole rather than re-deriving that logic a second time.
function PhonebookPane({ onCallNumber, onQuickCallNumber, maskNumbers, activeCustomerNumber }) {
  const [subTab, setSubTab] = useState('internal');
  const [query, setQuery] = useState('');

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Background polls are silent: no spinner, and a failed poll keeps the
  // current list instead of blanking it.
  const fetchAgents = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const resp = await getApiCall(API_AGENT_MY_INTERNAL_AGENTS);
      const list = Array.isArray(resp) ? resp : Array.isArray(resp?.agents) ? resp.agents : [];
      setAgents(list);
    } catch (err) {
      console.error('Phonebook fetch failed', err);
      if (!silent) setAgents([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subTab !== 'internal') return undefined;
    let isMounted = true;
    let timeoutId;
    let first = true;
    const poll = async () => {
      if (!isMounted) return;
      if (!document.hidden) await fetchAgents({ silent: !first });
      first = false;
      if (!isMounted) return;
      // 20s background poll so agent availability stays fresh.
      timeoutId = setTimeout(poll, 20000);
    };
    poll();
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchAgents, subTab]);

  const filteredAgents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => {
      const name = String(a?.name || '').toLowerCase();
      const ext = String(a?.extension || '').toLowerCase();
      const num = String(a?.number || '').toLowerCase();
      return name.includes(q) || ext.includes(q) || num.includes(q);
    });
  }, [agents, query]);

  const availabilityColor = useCallback((availability) => {
    if (availability === 'available') return '#22c55e';
    if (availability === 'busy') return '#ef4444';
    return '#acacac';
  }, []);

  const availabilityLabel = useCallback((availability) => {
    if (availability === 'available') return 'Available';
    if (availability === 'busy') return 'Not available';
    return 'Offline';
  }, []);

  const tabsBar = (
    <div className={`flex items-center gap-1 border-b ${HAIRLINE} bg-muted/20 px-2 py-1`}>
      {[
        { key: 'internal', label: 'Internal' },
        { key: 'external', label: 'External' },
      ].map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => setSubTab(t.key)}
          className={`rounded px-2 py-1 text-xs ${subTab === t.key ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground'}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  if (subTab === 'external') {
    return (
      <ContactsPane
        onCallNumber={onCallNumber}
        onQuickCallNumber={onQuickCallNumber}
        maskNumbers={maskNumbers}
        activeCustomerNumber={activeCustomerNumber}
        tabsBar={tabsBar}
      />
    );
  }

  return (
    <div className="cri-pane cri-pane--contacts">
      <div className="cri-panel">
        <div className="cri-panel__head">
          <FiBookOpen size={13} className="cri-panel__ico" />
          <span className="cri-panel__title">Phonebook</span>
          <span className="cri-panel__count">{filteredAgents.length || undefined}</span>
          <button
            type="button"
            onClick={() => fetchAgents()}
            disabled={loading}
            title="Refresh agents"
            className="cri-add-btn"
          >
            <FiRefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        {tabsBar}
          <>
            <div className={`flex items-center gap-1.5 border-b ${HAIRLINE} bg-muted/20 px-2.5 py-1.5`}>
              <FiSearch className="h-3 w-3 shrink-0 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search agent name, number or extension…"
                className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="cri-contact-list">
              {filteredAgents.length === 0 ? (
                <div className="cri-empty">
                  {loading ? 'Loading…' : query ? 'No agents match' : 'No agents in your call group or call centre'}
                </div>
              ) : (
                filteredAgents.map((a) => {
                  const isOffline = a?.availability === 'offline';
                  const dialTarget = !isOffline ? (a?.extension || a?.number || '') : '';
                  return (
                    <div key={a.id} className="cri-contact-row">
                      <div className="cri-contact-main" style={{ cursor: 'default' }}>
                        <div
                          title={availabilityLabel(a?.availability)}
                          style={{
                            width: 8,
                            height: 8,
                            minWidth: 8,
                            borderRadius: '50%',
                            background: availabilityColor(a?.availability),
                            marginRight: 6,
                            boxShadow: a?.availability === 'available' ? '0 0 6px rgba(34,197,94,0.6)' : 'none',
                          }}
                        />
                        <div className="cri-contact-avatar" aria-hidden>
                          {String(a?.name || 'A').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="cri-contact-body">
                          <span className="cri-contact-name">{a?.name || `Agent ${a.id}`}</span>
                          <span className="cri-contact-sub">
                            {a?.extension ? `x${a.extension}` : 'No extension'}
                            {a?.number
                              ? ` · ${a?.country_code ? `${a.country_code} ` : ''}${maskNumbers ? maskNumber(a.number) : a.number}`
                              : ''}
                          </span>
                        </div>
                      </div>
                      <div className="cri-contact-actions">
                        <span
                          className="cri-contact-sub"
                          style={{ color: availabilityColor(a?.availability), marginRight: 6 }}
                        >
                          {availabilityLabel(a?.availability)}
                        </span>
                        <button
                          type="button"
                          className="cri-icon-btn"
                          title={isOffline ? 'Agent offline' : dialTarget ? `Call ${a?.extension ? `x${dialTarget}` : (maskNumbers ? maskNumber(dialTarget) : dialTarget)}` : 'No extension or number on file'}
                          disabled={!dialTarget}
                          onClick={() => dialTarget && onQuickCallNumber?.(dialTarget)}
                        >
                          <FiPhoneCall size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
      </div>
    </div>
  );
}

function ContactsPane({ onCallNumber, onQuickCallNumber, maskNumbers, activeCustomerNumber, tabsBar = null }) {
  const activeCustomerKey = useMemo(() => lastTen(activeCustomerNumber), [activeCustomerNumber]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [moduleOps, setModuleOps] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [editingContact, setEditingContact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getApiCall(API_ALL_CONTACTS_LIST, {});
      setContacts(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.error('Contacts fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    const poll = async () => {
      if (!isMounted) return;
      if (!document.hidden) await fetchContacts();
      if (!isMounted) return;
      // 60s background poll; the header refresh button gives an instant one.
      timeoutId = setTimeout(poll, 60000);
    };
    poll();
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchContacts]);

  // ACL gate — same module_operations source AllContactsPage.tsx reads,
  // permissive (button shown) until ACL settings actually load.
  useEffect(() => {
    const user = getUser();
    const raw = user?.acl_settings?.module_operations;
    if (!raw) return;
    try {
      setModuleOps(typeof raw === 'string' ? JSON.parse(raw) : raw);
    } catch {
      // leave permissive default
    }
  }, []);

  const aclLoaded = Object.keys(moduleOps).length > 0;
  // Create/Edit/Delete were already gated on this exact module — Read
  // never was, so an agent with Contacts removed from their ACL entirely
  // could still see the full list (just couldn't add/edit/delete from it).
  const canRead = !aclLoaded || hasPermission(moduleOps, 'Contacts', 'read');
  const canCreate = !aclLoaded || hasPermission(moduleOps, 'Contacts', 'add');
  const canEdit = !aclLoaded || hasPermission(moduleOps, 'Contacts', 'edit');
  const isAgent = getUser()?.role === USER_ROLES.CALL_CENTRE_AGENT;
  const canDelete = !isAgent && (!aclLoaded || hasPermission(moduleOps, 'Contacts', 'delete'));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts.slice(0, 200);
    return contacts
      .filter((c) => {
        const name = contactDisplayName(c).toLowerCase();
        const num = String(c?.primary_number || c?.contact_number || c?.number || '').toLowerCase();
        return name.includes(q) || num.includes(q);
      })
      .slice(0, 200);
  }, [contacts, query]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await postApiCall(API_CONTACT_GROUP_MEMBERS_DELETE.replace(':id', deleteTarget.id), {
        id: deleteTarget.id,
      });
      toast.success('Contact deleted successfully');
      setDeleteTarget(null);
      fetchContacts();
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to delete contact');
    } finally {
      setDeleting(false);
    }
  };

  if (!canRead) {
    return (
      <div className="cri-pane cri-pane--contacts">
        <div className="cri-panel">
          <div className="cri-panel__head">
            <FiUsers size={13} className="cri-panel__ico" />
            <span className="cri-panel__title">Assigned contacts</span>
          </div>
          {tabsBar}
          <div className="cri-empty">ACL permission missing for Contacts.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cri-pane cri-pane--contacts">
      <div className="cri-panel">
        <div className="cri-panel__head">
          <FiUsers size={13} className="cri-panel__ico" />
          <span className="cri-panel__title">Assigned contacts</span>
          <span className="cri-panel__count">
            {filtered.length}
            {contacts.length !== filtered.length ? ` / ${contacts.length}` : ''}
          </span>
          <button
            type="button"
            onClick={fetchContacts}
            disabled={loading}
            title="Refresh contacts"
            className="cri-add-btn"
          >
            <FiRefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
          {canCreate ? (
            <button
              type="button"
              className="cri-add-btn"
              title="Add contact"
              onClick={() => {
                setFormMode('add');
                setEditingContact(null);
                setFormOpen(true);
              }}
            >
              <FiPlus size={12} />
            </button>
          ) : null}
        </div>
        {tabsBar}
        <div className="cri-panel__filter">
          <FiSearch size={11} className="cri-search__icon" />
          <input
            type="text"
            placeholder="Search contacts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="cri-search__input"
          />
        </div>
        <div className="cri-contact-list">
          {filtered.length === 0 ? (
            <div className="cri-empty">
              {loading ? 'Loading…' : 'No contacts assigned'}
            </div>
          ) : (
            filtered.map((c, i) => {
              const name = contactDisplayName(c);
              const num = c?.primary_number || c?.contact_number || c?.number || '';
              const group = c?.contact_group_name || c?.group_name || '';
              const email = findAdditionalDetail(c?.additional_details, 'email', 'emailaddress', 'emailid');
              // The agent is on the line with this exact contact right
              // now — editing/deleting their record mid-call (e.g. a slip
              // that renames or removes them) is the one moment it's
              // guaranteed to matter and be hard to undo cleanly.
              const isLiveCallContact = Boolean(
                activeCustomerKey
                  && [num, c?.alt1_number, c?.alt2_number].some((n) => n && lastTen(n) === activeCustomerKey),
              );
              return (
                <div key={c?.id ?? `${num}-${i}`} className="cri-contact-row">
                  <button
                    type="button"
                    className="cri-contact-main"
                    onClick={() => num && onCallNumber?.(num)}
                    title={num ? `Dial ${maskNumbers ? maskNumber(num) : num}` : ''}
                  >
                    <div className="cri-contact-avatar" aria-hidden>
                      {String(name).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="cri-contact-body">
                      <span className="cri-contact-name">{name}</span>
                      <span className="cri-contact-sub">
                        {num ? (maskNumbers ? maskNumber(num) : num) : '—'}
                        {group ? ` · ${group}` : ''}
                      </span>
                      {email && <span className="cri-contact-sub">{email}</span>}
                    </div>
                  </button>
                  <div className="cri-contact-actions">
                    <button
                      type="button"
                      className="cri-icon-btn"
                      title={num ? `Call ${maskNumbers ? maskNumber(num) : num}` : 'No number on file'}
                      disabled={!num}
                      onClick={() => num && onQuickCallNumber?.(num)}
                    >
                      <FiPhoneCall size={12} />
                    </button>
                    {canEdit || canDelete ? (
                    <>
                      {canEdit ? (
                        <button
                          type="button"
                          className="cri-icon-btn"
                          title={isLiveCallContact ? "Can't edit — this contact is on the active call" : `Edit ${name}`}
                          disabled={isLiveCallContact}
                          onClick={() => {
                            if (isLiveCallContact) return;
                            setFormMode('edit');
                            setEditingContact(c);
                            setFormOpen(true);
                          }}
                        >
                          <FiEdit2 size={12} />
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          className="cri-icon-btn cri-icon-btn--danger"
                          title={isLiveCallContact ? "Can't delete — this contact is on the active call" : `Delete ${name}`}
                          disabled={isLiveCallContact}
                          onClick={() => { if (!isLiveCallContact) setDeleteTarget(c); }}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      ) : null}
                    </>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ContactFormDialog
        open={formOpen}
        mode={formMode}
        initial={editingContact}
        onClose={() => setFormOpen(false)}
        onSaved={fetchContacts}
        maskNumbers={maskNumbers}
      />
      <DeleteContactDialog
        contact={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

const MOS_COLORS = {
  excellent: '#10b981',
  good: '#22c55e',
  fair: '#f59e0b',
  poor: '#f97316',
  bad: '#ef4444',
  unknown: 'var(--text-dim)',
};

// Small up/down trend-line icon next to the MOS value — a decorative
// zigzag, not a real historical plot (the store only ever keeps the
// latest sample, not a rolling series), colored to match the MOS reading.
function MosSparkline({ color }) {
  return (
    <svg width="26" height="14" viewBox="0 0 26 14" className="cri-mos-spark" aria-hidden>
      <polyline
        points="1,10 6,4 10,11 14,3 18,8 21,2 25,6"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SignalBars({ bars = 0, color }) {
  return (
    <span className="cri-signal-bars" aria-hidden>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="cri-signal-bar"
          style={{ height: 3 + i * 2.2, background: i <= bars ? color : undefined }}
        />
      ))}
    </span>
  );
}

function CallDetailsPane({ queueName, maskNumbers }) {
  const callState        = useCallStore((s) => s.callState);
  const activeCallMeta   = useCallStore((s) => s.activeCallMeta);
  const direction        = useCallStore((s) => s.direction);
  const activeCliNumber  = useCallStore((s) => s.activeCliNumber);
  const outgoingStatus   = useCallStore((s) => s.outgoingCallStatus);
  const customerRingingActive = useCallStore((s) => s.customerRingingActive);
  // Same resolvers CustomerProfileCard.tsx uses — this pane used to guess a
  // name straight from activeCallMeta fields that never actually carry one
  // (the name only ever lives on callerContact, the separately-fetched
  // contact-group-member record), so it always showed "—" here even while
  // the customer card and Recent Calls both had it.
  const callerContact    = useCallStore((s) => s.callerContact);
  // Live MOS/codec — sampled every tick during the call by
  // startWebrtcStatsMonitor (softphone/demo-1.ts) into this same store
  // field, the exact source CallQualityIndicator.tsx's badge reads; this
  // pane just surfaces it as plain data instead of a pass/fail badge.
  const callQuality      = useCallStore((s) => s.callQuality);

  const isActive = callState && callState !== 'idle';
  const bridgeTime = activeCallMeta?.bridge_time;

  const [talkSeconds, setTalkSeconds] = useState(0);
  useEffect(() => {
    if (!isActive || !bridgeTime) { setTalkSeconds(0); return undefined; }
    const bridgeMs = new Date(bridgeTime).getTime();
    if (Number.isNaN(bridgeMs)) return undefined;
    const tick = () => setTalkSeconds(Math.max(0, Math.floor((Date.now() - bridgeMs) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isActive, bridgeTime]);

  if (!isActive) {
    return (
      <div className="cri-pane cri-pane--details">
        <div className="cri-panel">
          <div className="cri-panel__head">
            <FiInfo size={13} className="cri-panel__ico" />
            <span className="cri-panel__title">Call details</span>
          </div>
          <div className="cri-details-empty">
            <FiInfo size={20} className="cri-details-empty__icon" />
            <div className="cri-details-empty__title">No active call</div>
            <div className="cri-details-empty__hint">
              Details of an incoming or outgoing call will appear here.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mosLabel = callQuality?.mos != null ? mosToLabel(callQuality.mos) : 'unknown';
  const mosColor = MOS_COLORS[mosLabel] || MOS_COLORS.unknown;
  const codec = callQuality?.inbound?.codec || callQuality?.outbound?.codec;
  const codecText = codec?.name
    ? `${codec.name}${codec.clockRate ? ` / ${Math.round(codec.clockRate / 1000)} kHz` : ''}`
    : '—';

  const rows = [
    { label: 'Call ID',          value: activeCallMeta?.call_sid || activeCallMeta?.callSid || '—' },
    { label: 'Direction',        value: (direction || '—').toString().toUpperCase() },
    { label: 'Call Group',       value: queueName || '—' },
    { label: 'Campaign',         value: activeCallMeta?.campaign_name || activeCallMeta?.campaign || '—' },
    { label: 'Talk time',        value: bridgeTime ? formatSeconds(talkSeconds) : '—' },
    {
      label: 'Customer number',
      value: (() => {
        const num = resolveCustomerNumber(callerContact, activeCallMeta);
        if (!num) return '—';
        return maskNumbers ? maskNumber(num) : num;
      })(),
    },
    { label: 'Customer name',    value: resolveCustomerName(callerContact, activeCallMeta) || '—' },
    {
      label: 'Alternate number 1',
      value: (() => {
        const num = callerContact?.alt1_number;
        if (!num) return '—';
        return maskNumbers ? maskNumber(num) : num;
      })(),
    },
    {
      label: 'Alternate number 2',
      value: (() => {
        const num = callerContact?.alt2_number;
        if (!num) return '—';
        return maskNumbers ? maskNumber(num) : num;
      })(),
    },
    {
      label: 'Email',
      value: findAdditionalDetail(callerContact?.additional_details, 'email', 'emailaddress', 'emailid') || '—',
    },
    { label: 'CLI',              value: activeCliNumber || activeCallMeta?.did_number || '—' },
    (() => {
      const phase = resolveCallPhase({
        direction,
        callState,
        outgoingCallStatus: outgoingStatus,
        customerRingingActive,
        hasBridge: Boolean(bridgeTime),
      });
      return { label: 'Phase', value: phase.text, evt: callPhaseEvt(phase) };
    })(),
    // Codec/MOS only ever come from startWebrtcStatsMonitor (WebRTC calls)
    // — a mobile-profile call has no browser media stats to sample, so
    // these always read "—" for it. Hidden instead of shown-permanently-
    // dashed, since a value that can never be true for an entire call type
    // isn't a real "detail," just dead space.
    codec?.name ? { label: 'Codec', value: codecText } : null,
    callQuality?.mos != null ? {
      label: 'MOS',
      value: callQuality.mos.toFixed(1),
      color: mosColor,
      icon: <MosSparkline color={mosColor} />,
    } : null,
  ].filter(Boolean);

  return (
    <div className="cri-pane cri-pane--details">
      <div className="cri-panel">
        <div className="cri-panel__head">
          <SignalBars bars={callQuality?.bars || 0} color={mosColor} />
          <span className="cri-panel__title">Call details</span>
          <span className={`call-evt call-evt--${callStateEvt(callState)} shrink-0`}>
            {String(callState)}
          </span>
        </div>
        <div className="cri-details-grid2">
          {rows.map((r) => (
            <div className="cri-details-cell" key={r.label}>
              <div className="cri-details-cell__label">{r.label}</div>
              <div
                className="cri-details-cell__value cri-details-cell__value--row"
                style={r.color ? { color: r.color } : undefined}
                title={String(r.value)}
              >
                {r.evt ? <span className={`call-evt call-evt--${r.evt}`}>{r.value}</span> : r.value}
                {r.icon}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatAnnouncementDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}

function AnnouncementsPane({ announcements, loading, onRefresh }) {
  if (announcements === undefined) return null;
  return (
    <div className="mt-2 rounded-lg bg-card p-2.5">
      <div className="mb-2 flex items-center gap-1.5">
        <FiBell size={12} className="text-primary" />
        <span className="text-xs font-semibold text-foreground">Announcements</span>
        {onRefresh ? (
          <button
            type="button"
            onClick={onRefresh}
            title="Refresh announcements"
            className="ml-auto flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
          >
            <FiRefreshCw size={10} className={loading ? 'animate-spin' : ''} />
          </button>
        ) : null}
      </div>
      <div className="ap-noscrollbar max-h-28 space-y-1.5 overflow-y-auto">
        {!announcements.length ? (
          <div className="py-2 text-center text-[11px] text-muted-foreground">No announcements</div>
        ) : (
          announcements.map((a) => (
            <div key={a.id} className="rounded-md bg-muted/40 px-2 py-1.5">
              <div className="text-[11px] font-semibold text-foreground">{a.heading}</div>
              {a.content ? <div className="mt-0.5 text-[10.5px] text-muted-foreground">{a.content}</div> : null}
              {a.created_at ? (
                <div className="mt-1 text-[9.5px] uppercase tracking-wide text-muted-foreground/70">
                  {formatAnnouncementDate(a.created_at)}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function CTIRightRail({
  callRecords = [],
  customerHistory,
  customerHistoryLoading,
  onCallNumber,
  onQuickCallNumber,
  activeCustomerNumber,
  queueName,
  maskNumbers,
  footer,
  announcements,
  announcementsLoading,
  onRefreshAnnouncements,
  contactNameByNumber,
}) {
  const [tab, setTab] = useState('history');

  return (
    <aside className="cti-right-rail" aria-label="Customer context">
      <div className="cri-tabs-card">
        <div className="cri-tabs" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`cri-tab${active ? ' cri-tab--active' : ''}`}
              >
                <Icon size={11} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="cri-body">
        {tab === 'history' && (
          <HistoryPane
            callRecords={callRecords}
            customerHistory={customerHistory}
            customerHistoryLoading={customerHistoryLoading}
            onCallNumber={onCallNumber}
            activeCustomerNumber={activeCustomerNumber}
            maskNumbers={maskNumbers}
            contactNameByNumber={contactNameByNumber}
          />
        )}
        {tab === 'phonebook' && (
          <PhonebookPane
            onCallNumber={onCallNumber}
            onQuickCallNumber={onQuickCallNumber}
            maskNumbers={maskNumbers}
            activeCustomerNumber={activeCustomerNumber}
          />
        )}
        {tab === 'call-details' && <CallDetailsPane queueName={queueName} maskNumbers={maskNumbers} />}
        {footer}
        <AnnouncementsPane
          announcements={announcements}
          loading={announcementsLoading}
          onRefresh={onRefreshAnnouncements}
        />
      </div>
    </aside>
  );
}
